import { useEffect, useState } from "react"
import { PaymentSettings } from "../../../types/payment"
import bbApi from "../../../api"

interface UsePaymentSettingsReturn {
  paymentSettings: PaymentSettings | null
  isLoading: boolean
  error: string | null
  loadPaymentSettings: () => Promise<void>
  handleRemovePaymentMethod: (paymentMethodId: string) => Promise<void>
  handleSetDefaultPaymentMethod: (paymentMethodId: string) => Promise<void>
}

export const usePaymentSettings = (
  orgId: string | undefined
): UsePaymentSettingsReturn => {
  const [paymentSettings, setPaymentSettings] =
    useState<PaymentSettings | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadPaymentSettings = async () => {
    if (!orgId) return

    setIsLoading(true)
    setError(null)

    try {
      const settings = await bbApi.payment.fetchPaymentSettings(orgId)
      console.log("Payment settings response:", settings)
      console.log("Payment methods:", settings?.payment_methods)
      setPaymentSettings(settings)
    } catch (err: any) {
      console.error("Failed to load payment settings:", err)
      setError(err.message || "Failed to load payment settings")
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadPaymentSettings()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId])

  const handleRemovePaymentMethod = async (paymentMethodId: string) => {
    if (!orgId) return
    if (!window.confirm("Are you sure you want to remove this payment method?"))
      return

    try {
      await bbApi.payment.removePaymentMethod({
        org_id: orgId,
        payment_method_id: paymentMethodId
      })
      await loadPaymentSettings()
    } catch (err: any) {
      alert(err.message || "Failed to remove payment method")
    }
  }

  const handleSetDefaultPaymentMethod = async (paymentMethodId: string) => {
    if (!orgId) return

    try {
      await bbApi.payment.setDefaultPaymentMethod({
        org_id: orgId,
        payment_method_id: paymentMethodId
      })
      await loadPaymentSettings()
    } catch (err: any) {
      alert(err.message || "Failed to set default payment method")
    }
  }

  return {
    paymentSettings,
    isLoading,
    error,
    loadPaymentSettings,
    handleRemovePaymentMethod,
    handleSetDefaultPaymentMethod
  }
}
